import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoCloseOutline, IoFileTrayOutline, IoFolderOutline, IoHomeOutline, IoSearchOutline } from 'react-icons/io5';
import { normalizeFolderName } from './fileLibrary';

/**
 * In-page confirmation (no window.confirm). Cancel has focus so Enter is safe.
 * status: a progress line while busy ("Moving 3 of 10…").
 */
export function ConfirmDialog({ title, message, confirmLabel = 'Confirm', danger = false, busy = false, status, onConfirm, onCancel }) {
  const cancelRef = useRef(null);
  useEffect(() => { cancelRef.current?.focus(); }, []);
  return (
    <div className="fl-layer" onClick={busy ? undefined : onCancel}>
      <div
        className="fl-dialog fl-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="fl-confirm-title"
        aria-describedby={message ? 'fl-confirm-message' : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="fl-confirm-title" className="fl-dialog-title">{title}</h3>
        {message ? <p id="fl-confirm-message" className="fl-dialog-text">{message}</p> : null}
        <div className="fl-dialog-actions">
          {status !== undefined ? <span className="fl-dialog-status" aria-live="polite">{status}</span> : null}
          <button type="button" ref={cancelRef} className="fl-btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className={`fl-btn ${danger ? 'fl-btn-danger' : 'fl-btn-primary'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? <span className="fl-spinner" aria-hidden="true" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Pick a destination folder. Plain buttons (no drag), so it works on touch.
 *   allowUnfiled: offer "Unfiled" (take a file out of every folder)
 *   allowRoot:    offer "Top level" (move a folder to the root)
 *   excludeIds:   folders that can't be picked (a folder itself and its subtree)
 */
export function FolderPickerDialog({
  title,
  description,
  folderIndex,
  excludeIds,
  currentFolderIds,
  allowUnfiled = false,
  allowRoot = false,
  confirmLabel = 'Move here',
  busy = false,
  onConfirm,
  onCancel,
}) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(undefined); // undefined = nothing picked; null = unfiled/root
  const searchRef = useRef(null);
  useEffect(() => { searchRef.current?.focus(); }, []);

  const rows = useMemo(() => {
    const out = [];
    const excluded = excludeIds || new Set();
    const walk = (parentId, depth) => {
      (folderIndex.children.get(parentId) || []).forEach((folder) => {
        if (excluded.has(folder.folder_id)) return;
        out.push({ folder, depth });
        walk(folder.folder_id, depth + 1);
      });
    };
    walk('', 0);
    const q = normalizeFolderName(query);
    if (!q) return out;
    return out
      .filter(({ folder }) => normalizeFolderName(folderIndex.pathOf(folder.folder_id)).includes(q))
      .map(({ folder }) => ({ folder, depth: 0, showPath: true }));
  }, [excludeIds, folderIndex, query]);

  const current = currentFolderIds || new Set();
  const pickedLabel = picked === null
    ? (allowUnfiled ? 'Unfiled' : 'Top level')
    : picked ? folderIndex.pathOf(picked) : '';

  return (
    <div className="fl-layer" onClick={busy ? undefined : onCancel}>
      <div className="fl-dialog fl-picker" role="dialog" aria-modal="true" aria-labelledby="fl-picker-title" onClick={(e) => e.stopPropagation()}>
        <div className="fl-dialog-head">
          <h3 id="fl-picker-title" className="fl-dialog-title">{title}</h3>
          <button type="button" className="fl-icon-btn" onClick={onCancel} aria-label="Close folder picker" disabled={busy}>
            <IoCloseOutline size={18} />
          </button>
        </div>
        {description ? <p className="fl-dialog-text">{description}</p> : null}
        <label className="fl-picker-search">
          <IoSearchOutline size={15} aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a folder"
            aria-label="Find a folder"
          />
        </label>
        <div className="fl-picker-list" role="listbox" aria-label="Folders">
          {allowUnfiled && !query ? (
            <button
              type="button"
              role="option"
              aria-selected={picked === null}
              className={`fl-picker-row${picked === null ? ' is-picked' : ''}`}
              onClick={() => setPicked(null)}
            >
              <IoFileTrayOutline size={16} aria-hidden="true" />
              <span className="fl-picker-name">Unfiled</span>
              <span className="fl-picker-hint">Take it out of every folder</span>
            </button>
          ) : null}
          {allowRoot && !query ? (
            <button
              type="button"
              role="option"
              aria-selected={picked === null}
              className={`fl-picker-row${picked === null ? ' is-picked' : ''}`}
              onClick={() => setPicked(null)}
            >
              <IoHomeOutline size={16} aria-hidden="true" />
              <span className="fl-picker-name">Top level</span>
            </button>
          ) : null}
          {rows.map(({ folder, depth, showPath }) => {
            const isCurrent = current.has(folder.folder_id);
            const hint = showPath
              ? folderIndex.pathOf(folder.folder_id)
              : folderIndex.hintOf(folder.folder_id);
            return (
              <button
                key={folder.folder_id}
                type="button"
                role="option"
                aria-selected={picked === folder.folder_id}
                className={`fl-picker-row${picked === folder.folder_id ? ' is-picked' : ''}`}
                style={{ paddingLeft: `${12 + depth * 18}px` }}
                onClick={() => setPicked(folder.folder_id)}
              >
                <IoFolderOutline size={16} aria-hidden="true" className="fl-folder-glyph" />
                <span className="fl-picker-name">{folder.name}</span>
                {hint ? <span className="fl-picker-hint">{hint}</span> : null}
                {isCurrent ? <span className="fl-chip fl-chip-muted">Current</span> : null}
              </button>
            );
          })}
          {rows.length === 0 && (query || (!allowUnfiled && !allowRoot)) ? (
            <div className="fl-empty fl-empty-compact">{query ? 'No folders match.' : 'No other folders yet.'}</div>
          ) : null}
        </div>
        <div className="fl-dialog-actions">
          <span className="fl-picker-picked" aria-live="polite">{pickedLabel ? `To: ${pickedLabel}` : ''}</span>
          <button type="button" className="fl-btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="fl-btn fl-btn-primary"
            disabled={busy || picked === undefined}
            onClick={() => onConfirm(picked)}
          >
            {busy ? <span className="fl-spinner" aria-hidden="true" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Ask for a folder name (create / rename). Errors show in place; the dialog stays open. */
export function NameDialog({ title, label = 'Folder name', initialValue = '', confirmLabel = 'Save', busy = false, error, onSubmit, onCancel }) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  const trimmed = value.trim();
  return (
    <div className="fl-layer" onClick={busy ? undefined : onCancel}>
      <form
        className="fl-dialog fl-name-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fl-name-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed && !busy) onSubmit(trimmed);
        }}
      >
        <h3 id="fl-name-title" className="fl-dialog-title">{title}</h3>
        <label className="fl-field">
          <span>{label}</span>
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={busy}
            aria-invalid={error ? 'true' : undefined}
            maxLength={200}
          />
        </label>
        {error ? <p className="fl-field-error" role="alert">{error}</p> : null}
        <div className="fl-dialog-actions">
          <button type="button" className="fl-btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="fl-btn fl-btn-primary" disabled={busy || !trimmed}>
            {busy ? <span className="fl-spinner" aria-hidden="true" /> : null}
            {confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
